/**
 * services/adsApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Ad listings, categories, and job board methods.
 *
 * METHODS:
 *   Ads:        fetchAds, fetchMyAds, fetchMyStats, getAds, getAdById,
 *               postAd, createAd, updateAd, deleteAd
 *   Categories: getCategories
 *   Jobs:       fetchJobs, getJobs, postJob, createJob
 *
 * NOTE: fetchMyAds() is the fast profile loader — hits ?seller=me&all=true
 *   so the backend filters in SQL. Only use fetchAds() for the public browse
 *   view (ProductsView), never for the profile/selling tab.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

function _adminHeaders(token) {
  const t = token || _getToken();
  return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
}

// ═════════════════════════════════════════════════════════════════════════════
// MODULE-LEVEL PROMISE CACHE
// ─────────────────────────────────────────────────────────────────────────────
// These promises are stored at module scope — meaning they survive across
// React re-renders and component mount/unmount cycles. The first caller kicks
// off the fetch; every subsequent caller gets the SAME in-flight promise back.
//
// On a direct page reload:
//   • adsApi.js is imported by api.js which is imported early in the bundle.
//   • warmProfileCache() fires at the bottom of this file, immediately when
//     the JS module is evaluated — before React has even mounted a component.
//   • By the time ProfilePage mounts and calls fetchMyAds(), the fetch is
//     already in flight (or done). The await returns instantly.
// ═════════════════════════════════════════════════════════════════════════════

let _myAdsPromise   = null;
let _myStatsPromise = null;

/** Start pre-fetching my ads — idempotent, safe to call multiple times. */
export function warmProfileCache() {
  const token = _getToken();
  if (!token) return; // not logged in — nothing to prefetch

  if (!_myAdsPromise) {
    console.log('🔥 warmProfileCache: kicking off fetchMyAds prefetch');
    _myAdsPromise = _fetchMyAdsRaw(token).catch(err => {
      console.error('❌ warmProfileCache: fetchMyAds failed', err);
      _myAdsPromise = null; // allow retry
      return [];
    });
  }

  if (!_myStatsPromise) {
    console.log('🔥 warmProfileCache: kicking off fetchMyStats prefetch');
    _myStatsPromise = _fetchMyStatsRaw(token).catch(err => {
      console.error('❌ warmProfileCache: fetchMyStats failed', err);
      _myStatsPromise = null;
      return null;
    });
  }
}

/** Clear the cache — call on logout or after mutating ops (create/delete ad). */
export function invalidateProfileCache() {
  console.log('🗑️ invalidateProfileCache: clearing cache');
  _myAdsPromise   = null;
  _myStatsPromise = null;
}

// Raw fetch helpers — no `this`, called by the cache layer and also by methods
async function _fetchMyAdsRaw(token) {
  const headers = { 'Authorization': `Token ${token}` };
  const res = await fetch(`${API_BASE_URL}/ads/?seller=me&all=true`, { headers });
  if (!res.ok) throw new Error(`fetchMyAds failed: ${res.status}`);
  const data = await res.json();
  const ads = Array.isArray(data) ? data : (data.results || data.ads || data.data || []);
  console.log(`✅ _fetchMyAdsRaw: ${ads.length} ads`);
  return ads;
}

async function _fetchMyStatsRaw(token) {
  // ⚠️ /api/ads/my-stats/ endpoint not yet implemented on backend.
  // Return null immediately so the fallback path runs without hitting the
  // network — this eliminates the red 404 error in DevTools entirely.
  void token; // suppress unused-var lint
  return null;
}

// ── Auto-warm on module load — fires the instant this file is imported ────────
// If the user is logged in, the fetch starts before any component mounts.
warmProfileCache();

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {

  // ===========================================================================
  // MY ADS — fast profile loader (only the current user's ads, server-filtered)
  // ===========================================================================

  async fetchMyAds() {
    try {
      const token = this._getToken();
      if (!token) {
        console.warn('⚠️ fetchMyAds: no auth token, returning []');
        return [];
      }

      // Use the module-level cache — if warmProfileCache() already started a
      // fetch, this awaits the same in-flight promise instead of making a new request.
      if (!_myAdsPromise) {
        console.log('📡 fetchMyAds: cache miss — starting fresh fetch');
        _myAdsPromise = _fetchMyAdsRaw(token).catch(err => {
          _myAdsPromise = null; // bust cache on error so next call retries
          throw err;
        });
      } else {
        console.log('⚡ fetchMyAds: cache hit — awaiting in-flight/resolved promise');
      }

      const ads = await _myAdsPromise;
      console.log(`✅ fetchMyAds: ${ads.length} ads (from cache)`);
      return ads.map(ad => this.transformAdData(ad));
    } catch (error) {
      console.error('❌ fetchMyAds error:', error);
      return [];
    }
  },

  // ===========================================================================
  // MY STATS — single aggregated request for the selling tab stats bar
  // ===========================================================================

  async fetchMyStats() {
    try {
      const token = this._getToken();
      if (!token) return null;

      if (!_myStatsPromise) {
        console.log('📡 fetchMyStats: cache miss — starting fresh fetch');
        _myStatsPromise = _fetchMyStatsRaw(token).catch(err => {
          _myStatsPromise = null;
          throw err;
        });
      } else {
        console.log('⚡ fetchMyStats: cache hit');
      }

      return await _myStatsPromise;
    } catch (error) {
      console.error('❌ fetchMyStats error:', error);
      return null;
    }
  },

  // ===========================================================================
  // ALL ADS — paginated public browse (ProductsView only, NOT the profile tab)
  // ===========================================================================

  async fetchAds() {
    try {
      const token = this._getToken();
      const headers = token ? { 'Authorization': `Token ${token}` } : {};

      let allAds = [];
      let url = `${API_BASE_URL}/ads/`;

      while (url) {
        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error(`fetchAds failed: ${response.status}`);
        const data = await response.json();

        if (Array.isArray(data)) {
          allAds = data;
          url = null;
        } else {
          allAds = allAds.concat(data.results || data.ads || data.data || []);
          url = data.next || null;
        }
      }

      console.log(`✅ fetchAds: ${allAds.length} total ads`);
      return allAds.map(ad => this.transformAdData(ad));
    } catch (error) {
      console.error('❌ fetchAds error:', error);
      return [];
    }
  },

  async getAds(filters = {}) {
    try {
      const token = this._getToken();
      const headers = token ? { 'Authorization': `Token ${token}` } : {};
      const params = new URLSearchParams(filters);
      const res = await fetch(`${API_BASE_URL}/ads/?${params}`, { headers });
      const data = await res.json();
      let arr = [];
      if (Array.isArray(data))  arr = data;
      else if (data?.results)   arr = data.results;
      else if (data?.ads)       arr = data.ads;
      else if (data?.data)      arr = data.data;
      return arr.map(ad => this.transformAdData(ad));
    } catch (error) { console.error('getAds error:', error); throw error; }
  },

  async getAdById(id) {
    try {
      const token = this._getToken();
      const headers = token ? { 'Authorization': `Token ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/ads/${id}/`, { headers });
      if (!res.ok) throw new Error(`getAdById failed: ${res.status}`);
      return this.transformAdData(await res.json());
    } catch (error) { console.error('❌ getAdById error:', error); return null; }
  },

  async postAd(formData, token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/ads/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${t}` },
        body: formData,
      });
      if (!res.ok) console.error('❌ postAd failed:', await res.json().catch(() => ({})));
      else { console.log('✅ Ad posted successfully'); invalidateProfileCache(); }
      return res;
    } catch (error) { console.error('❌ postAd error:', error); throw error; }
  },

  async createAd(adData) {
    try {
      const token = this._getToken();
      const res = await fetch(`${API_BASE_URL}/ads/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}` },
        body: adData,
      });
      return await res.json();
    } catch (error) { console.error('createAd error:', error); throw error; }
  },

  async updateAd(id, adData) {
    try {
      const token = this._getToken();
      const isFormData = adData instanceof FormData;
      const res = await fetch(`${API_BASE_URL}/ads/${id}/`, {
        method: 'PATCH',
        headers: isFormData
          ? { 'Authorization': `Token ${token}` }
          : { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: isFormData ? adData : JSON.stringify(adData),
      });
      if (!res.ok) throw new Error(`updateAd failed: ${res.status}`);
      return await res.json();
    } catch (error) { console.error('❌ updateAd error:', error); throw error; }
  },

  async deleteAd(id) {
    try {
      const token = this._getToken();
      const res = await fetch(`${API_BASE_URL}/ads/${id}/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) throw new Error(`deleteAd failed: ${res.status}`);
      invalidateProfileCache();
      return { success: true };
    } catch (error) {
      console.error('❌ deleteAd error:', error);
      return { success: false, error: error.message };
    }
  },

  // ===========================================================================
  // CATEGORIES
  // ===========================================================================

  async getCategories() {
    try {
      const res = await fetch(`${API_BASE_URL}/categories/`);
      return await res.json();
    } catch (error) { console.error('getCategories error:', error); throw error; }
  },

  // ===========================================================================
  // JOBS
  // ===========================================================================

  async fetchJobs() {
    try {
      const res = await fetch(`${API_BASE_URL}/jobs/`);
      if (!res.ok) throw new Error(`fetchJobs failed: ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data)) return data;
      if (data?.results)       return data.results;
      if (data?.jobs)          return data.jobs;
      return data;
    } catch (error) { console.error('❌ fetchJobs error:', error); return []; }
  },

  async getJobs(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const res = await fetch(`${API_BASE_URL}/jobs/?${params}`);
      return await res.json();
    } catch (error) { console.error('getJobs error:', error); throw error; }
  },

  async postJob(jobData, token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/jobs/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(jobData),
      });
      if (!res.ok) console.error('❌ postJob failed:', res.status);
      else console.log('✅ Job posted successfully');
      return res;
    } catch (error) { console.error('❌ postJob error:', error); throw error; }
  },

  async createJob(jobData) {
    try {
      const token = this._getToken();
      const res = await fetch(`${API_BASE_URL}/jobs/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(jobData),
      });
      return await res.json();
    } catch (error) { console.error('createJob error:', error); throw error; }
  },

  // AD LIKES
};






























// /**
//  * services/adsApi.js
//  * ─────────────────────────────────────────────────────────────────────────────
//  * Ad listings, categories, and job board methods.
//  *
//  * METHODS:
//  *   Ads:        fetchAds, getAds, getAdById, postAd, createAd, updateAd, deleteAd
//  *   Categories: getCategories
//  *   Jobs:       fetchJobs, getJobs, postJob, createJob
//  *
//  * NOTE: fetchAds() is the main paginated loader — follows Django's `next` URL
//  *   until all pages are fetched. Used by ProfilePage.loadUserData and ProductsView.
//  * ─────────────────────────────────────────────────────────────────────────────
//  */

// import { API_BASE_URL } from './apiUtils.js';

// function _getToken() {
//   return (
//     localStorage.getItem('authToken')  ||
//     localStorage.getItem('token')      ||
//     localStorage.getItem('adminToken') ||
//     localStorage.getItem('admin_token') ||
//     localStorage.getItem('auth_token') ||
//     sessionStorage.getItem('authToken') ||
//     sessionStorage.getItem('token')    ||
//     sessionStorage.getItem('adminToken')
//   );
// }

// function _adminHeaders(token) {
//   const t = token || _getToken();
//   return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
// }

// // ── Methods — exported as a plain object, spread into ApiService in api.js ────
// export const methods = {
// async fetchAds() {
//     try {
//       const token = this._getToken();
//       const headers = token ? { 'Authorization': `Token ${token}` } : {};
//       console.log('🔍 fetchAds | auth:', !!token);

//       let allAds = [];
//       let url = `${API_BASE_URL}/ads/`;

//       while (url) {
//         const response = await fetch(url, { headers });
//         if (!response.ok) throw new Error(`fetchAds failed: ${response.status}`);
//         const data = await response.json();

//         if (Array.isArray(data)) {
//           // Non-paginated response
//           allAds = data;
//           url = null;
//         } else {
//           // Paginated — accumulate results and follow `next`
//           allAds = allAds.concat(data.results || data.ads || data.data || []);
//           url = data.next || null;
//         }
//       }

//       console.log(`✅ fetchAds: ${allAds.length} total ads`);
//       return allAds.map(ad => this.transformAdData(ad));
//     } catch (error) { console.error('❌ fetchAds error:', error); return []; }
//   },

//   async getAds(filters = {}) {
//     try {
//       const token = this._getToken();
//       const headers = token ? { 'Authorization': `Token ${token}` } : {};
//       const params = new URLSearchParams(filters);
//       const res = await fetch(`${API_BASE_URL}/ads/?${params}`, { headers });
//       const data = await res.json();
//       let arr = [];
//       if (Array.isArray(data))  arr = data;
//       else if (data?.results)   arr = data.results;
//       else if (data?.ads)       arr = data.ads;
//       else if (data?.data)      arr = data.data;
//       return arr.map(ad => this.transformAdData(ad));
//     } catch (error) { console.error('getAds error:', error); throw error; }
//   },

//   async getAdById(id) {
//     try {
//       const token = this._getToken();
//       const headers = token ? { 'Authorization': `Token ${token}` } : {};
//       const res = await fetch(`${API_BASE_URL}/ads/${id}/`, { headers });
//       if (!res.ok) throw new Error(`getAdById failed: ${res.status}`);
//       return this.transformAdData(await res.json());
//     } catch (error) { console.error('❌ getAdById error:', error); return null; }
//   },

//   async postAd(formData, token) {
//     try {
//       const t = token || this._getToken();
//       const res = await fetch(`${API_BASE_URL}/ads/`, {
//         method: 'POST',
//         headers: { 'Authorization': `Token ${t}` },
//         body: formData,
//       });
//       if (!res.ok) console.error('❌ postAd failed:', await res.json().catch(() => ({})));
//       else console.log('✅ Ad posted successfully');
//       return res;
//     } catch (error) { console.error('❌ postAd error:', error); throw error; }
//   },

//   async createAd(adData) {
//     try {
//       const token = this._getToken();
//       const res = await fetch(`${API_BASE_URL}/ads/`, {
//         method: 'POST',
//         headers: { 'Authorization': `Token ${token}` },
//         body: adData,
//       });
//       return await res.json();
//     } catch (error) { console.error('createAd error:', error); throw error; }
//   },

//   async updateAd(id, adData) {
//     try {
//       const token = this._getToken();
//       const isFormData = adData instanceof FormData;
//       const res = await fetch(`${API_BASE_URL}/ads/${id}/`, {
//         method: 'PATCH',
//         headers: isFormData
//           ? { 'Authorization': `Token ${token}` }
//           : { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
//         body: isFormData ? adData : JSON.stringify(adData),
//       });
//       if (!res.ok) throw new Error(`updateAd failed: ${res.status}`);
//       return await res.json();
//     } catch (error) { console.error('❌ updateAd error:', error); throw error; }
//   },

//   async deleteAd(id) {
//     try {
//       const token = this._getToken();
//       const res = await fetch(`${API_BASE_URL}/ads/${id}/`, {
//         method: 'DELETE',
//         headers: { 'Authorization': `Token ${token}` },
//       });
//       if (!res.ok) throw new Error(`deleteAd failed: ${res.status}`);
//       return { success: true };
//     } catch (error) { console.error('❌ deleteAd error:', error); return { success: false, error: error.message }; }
//   },
// // CATEGORIES

//   async getCategories() {
//     try {
//       const res = await fetch(`${API_BASE_URL}/categories/`);
//       return await res.json();
//     } catch (error) { console.error('getCategories error:', error); throw error; }
//   },
// // JOBS

//   async fetchJobs() {
//     try {
//       const res = await fetch(`${API_BASE_URL}/jobs/`);
//       if (!res.ok) throw new Error(`fetchJobs failed: ${res.status}`);
//       const data = await res.json();
//       if (Array.isArray(data)) return data;
//       if (data?.results)       return data.results;
//       if (data?.jobs)          return data.jobs;
//       return data;
//     } catch (error) { console.error('❌ fetchJobs error:', error); return []; }
//   },

//   async getJobs(filters = {}) {
//     try {
//       const params = new URLSearchParams(filters);
//       const res = await fetch(`${API_BASE_URL}/jobs/?${params}`);
//       return await res.json();
//     } catch (error) { console.error('getJobs error:', error); throw error; }
//   },

//   async postJob(jobData, token) {
//     try {
//       const t = token || this._getToken();
//       const res = await fetch(`${API_BASE_URL}/jobs/`, {
//         method: 'POST',
//         headers: { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' },
//         body: JSON.stringify(jobData),
//       });
//       if (!res.ok) console.error('❌ postJob failed:', res.status);
//       else console.log('✅ Job posted successfully');
//       return res;
//     } catch (error) { console.error('❌ postJob error:', error); throw error; }
//   },

//   async createJob(jobData) {
//     try {
//       const token = this._getToken();
//       const res = await fetch(`${API_BASE_URL}/jobs/`, {
//         method: 'POST',
//         headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
//         body: JSON.stringify(jobData),
//       });
//       return await res.json();
//     } catch (error) { console.error('createJob error:', error); throw error; }
//   }

//   // AD LIKES
// };